import { useEffect, useState } from "react";
import api from "@/lib/api";
import { compact, inr, CHANNEL_COLORS } from "@/lib/format";
import { StatCard, Skeleton } from "@/components/Shared";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown } from "lucide-react";

export default function Settlements() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/dashboard/settlements").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="space-y-6"><Skeleton className="h-28" /><Skeleton className="h-96" /></div>;

  const rows = data.settlements.filter((s) => filter === "All" || s.channel === filter);

  return (
    <div className="space-y-6" data-testid="settlements-page">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total Paid Out" value={compact(data.summary.total_paid)} sub="last 8 cycles" testId="settlement-paid" />
        <StatCard label="Pending Payout" value={compact(data.summary.pending)} sub="awaiting settlement" testId="settlement-pending" />
        <StatCard label="Next Payout" value={data.summary.next_payout_date || "—"} sub="expected date" testId="settlement-next" />
      </div>
      {data.insights ? (
        <div className={`border rounded-lg p-5 flex items-start gap-3 ${data.insights.net_trend_pct >= 0 ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-900" : "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-900"}`} data-testid="settlement-insights">
          {data.insights.net_trend_pct >= 0
            ? <TrendingUp className="h-5 w-5 text-green-600 mt-0.5 shrink-0" aria-hidden="true" />
            : <TrendingDown className="h-5 w-5 text-red-600 mt-0.5 shrink-0" aria-hidden="true" />}
          <div>
            <p className="text-sm font-semibold">Settlement trend: {data.insights.net_trend_pct >= 0 ? "UP" : "DOWN"} {Math.abs(data.insights.net_trend_pct)}%</p>
            <p className="text-sm text-muted-foreground mt-1">{data.insights.message}</p>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="settlement-insights-placeholder">Trend insights appear once at least 4 settlement cycles are synced (finance backfill runs automatically).</p>
      )}
      <div className="flex gap-2 flex-wrap">
        {["All", "Amazon", "Flipkart", "Website"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`settlement-filter-${f.toLowerCase()}`}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              filter === f ? "bg-blue-600 text-white border-blue-600" : "border-border text-muted-foreground hover:text-foreground bg-card"
            }`}>
            {f}
          </button>
        ))}
      </div>
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="settlements-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              {["Settlement ID", "Channel", "Period", "Orders", "Gross Sales", "Fees", "Refunds", "Net Payout", "Payout Date", "Status"].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`settlement-row-${s.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{s.id}</td>
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2 font-medium">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHANNEL_COLORS[s.channel] }} />{s.channel}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-muted-foreground text-xs">{s.period_start} → {s.period_end}</td>
                <td className="px-4 py-2.5">{s.orders}</td>
                <td className="px-4 py-2.5">{inr(s.gross)}</td>
                <td className="px-4 py-2.5 text-red-600">-{inr(s.fees)}</td>
                <td className="px-4 py-2.5 text-red-600">-{inr(s.refunds)}</td>
                <td className="px-4 py-2.5 font-semibold text-green-600">{inr(s.net)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{s.payout_date}</td>
                <td className="px-4 py-2.5">
                  <Badge className={`border-0 font-semibold ${s.status === "Paid" ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"}`}>
                    {s.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
